import React from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Switch,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useSocialPolicy } from "../hooks/useSocialSync";
import { SocialSyncPolicy } from "../types/social.types";

const ToggleRow = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");

  return (
    <HStack
      justify="space-between"
      align="start"
      border="1px solid"
      borderColor={borderColor}
      rounded="xl"
      p={4}
      bg={cardBg}
    >
      <Box>
        <Text fontWeight="semibold" color={headingColor}>
          {label}
        </Text>
        {description && (
          <Text fontSize="sm" color={mutedText}>
            {description}
          </Text>
        )}
      </Box>

      <Switch
        isChecked={checked}
        onChange={(event) => onChange(event.target.checked)}
        colorScheme="teal"
      />
    </HStack>
  );
};

export const SocialAutomationSettings: React.FC = () => {
  const toast = useToast();
  const { policy, setPolicy, saving, save, error } = useSocialPolicy();

  const mutedText = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const inputBg = useColorModeValue("white", "gray.900");
  const inputTextColor = useColorModeValue("gray.800", "whiteAlpha.900");

  const update = <K extends keyof SocialSyncPolicy>(
    key: K,
    value: SocialSyncPolicy[K],
  ) => {
    setPolicy({ ...policy, [key]: value });
  };

  const handleSave = async () => {
    try {
      await save(policy);

      toast({
        title: "Automation settings saved",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: err.message || "Failed to save settings",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <VStack align="stretch" spacing={5}>
      <Box>
        <Text fontSize="xl" fontWeight="bold" color={headingColor}>
          Social Automation Settings
        </Text>
        <Text color={mutedText}>
          Choose how much PickDengu should automate after seller approval.
        </Text>
      </Box>

      {error && <Text color="red.500">{error}</Text>}

      <FormControl maxW="420px">
        <FormLabel>Automation level</FormLabel>
        <Select
          value={policy.automationLevel}
          bg={inputBg}
          color={inputTextColor}
          borderColor={borderColor}
          onChange={(event) =>
            update("automationLevel", event.target.value as any)
          }
        >
          <option value="manual">Manual - approve everything</option>
          <option value="semi_auto">
            Semi-auto - generate automatically, approve publishing
          </option>
          <option value="autopilot">Autopilot - verified sellers only</option>
        </Select>
      </FormControl>

      <Divider borderColor={borderColor} />

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <ToggleRow
          label="Auto import social products"
          description="Pull allowed listings/media from connected accounts."
          checked={policy.autoImport}
          onChange={(value) => update("autoImport", value)}
        />
        <ToggleRow
          label="Auto enhance images"
          description="Clean and improve uploaded product images."
          checked={policy.autoEnhanceImages}
          onChange={(value) => update("autoEnhanceImages", value)}
        />
        <ToggleRow
          label="Auto generate descriptions"
          description="Create title, description and social captions."
          checked={policy.autoGenerateDescriptions}
          onChange={(value) => update("autoGenerateDescriptions", value)}
        />
        <ToggleRow
          label="Auto publish to PickDengu"
          description="Recommended only for trusted sellers."
          checked={policy.autoPublishToPickDengu}
          onChange={(value) => update("autoPublishToPickDengu", value)}
        />
        <ToggleRow
          label="Publish to Facebook"
          checked={policy.publishToFacebook}
          onChange={(value) => update("publishToFacebook", value)}
        />
        <ToggleRow
          label="Publish to Instagram"
          checked={policy.publishToInstagram}
          onChange={(value) => update("publishToInstagram", value)}
        />
        <ToggleRow
          label="Publish to TikTok"
          checked={policy.publishToTikTok}
          onChange={(value) => update("publishToTikTok", value)}
        />
        <ToggleRow
          label="Sync to Google Merchant"
          checked={policy.publishToGoogleMerchant}
          onChange={(value) => update("publishToGoogleMerchant", value)}
        />
        <ToggleRow
          label="WhatsApp campaigns ready"
          description="Keeps WhatsApp connected for templates/campaign flows. Messaging still follows Meta App Review limits."
          checked={policy.publishToWhatsApp}
          onChange={(value) => update("publishToWhatsApp", value)}
        />
        <ToggleRow
          label="Require approval before social publish"
          checked={policy.requireApprovalBeforeSocialPublish}
          onChange={(value) =>
            update("requireApprovalBeforeSocialPublish", value)
          }
        />
        <ToggleRow
          label="Require approval before PickDengu publish"
          checked={policy.requireApprovalBeforePickDenguPublish}
          onChange={(value) =>
            update("requireApprovalBeforePickDenguPublish", value)
          }
        />
      </SimpleGrid>

      <FormControl maxW="240px">
        <FormLabel>Max auto posts per day</FormLabel>
        <Input
          type="number"
          min={1}
          max={50}
          value={policy.maxAutoPostsPerDay || 3}
          bg={inputBg}
          color={inputTextColor}
          borderColor={borderColor}
          onChange={(event) =>
            update("maxAutoPostsPerDay", Number(event.target.value || 3))
          }
        />
      </FormControl>

      <Button
        colorScheme="teal"
        onClick={handleSave}
        isLoading={saving}
        alignSelf="flex-start"
      >
        Save automation settings
      </Button>
    </VStack>
  );
};
